import { useEffect, useState, type RefObject } from 'react';

/**
 * Keeps an always-mounted overlay rendered until its exit transition has
 * finished. Style the element with `[data-state='open' | 'closed']` and give
 * it `hidden={!present}`; the CSS transitions run in both directions and can
 * be interrupted, because a reopened overlay simply transitions back.
 *
 * Returns `present` (true while open or leaving) and the `state` to put on
 * the element.
 */
export function usePresence(open: boolean, ref: RefObject<HTMLElement | null>) {
  const [present, setPresent] = useState(open);
  if (open && !present) setPresent(true);

  useEffect(() => {
    if (open) return;
    let active = true;
    void afterAnimations(ref.current).then(() => {
      if (active) setPresent(false);
    });
    return () => {
      active = false;
    };
  }, [open, ref]);

  return { present: open || present, state: open ? ('open' as const) : ('closed' as const) };
}

/** Resolves once all running animations and transitions on the element have ended. */
export function afterAnimations(element: HTMLElement | null): Promise<unknown> {
  const animations =
    element && typeof element.getAnimations === 'function' ? element.getAnimations() : [];
  return Promise.allSettled(animations.map((animation) => animation.finished));
}
